import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Clock, CalendarOff, CalendarCheck, Plus, Trash2 } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

export default function SchedulePage() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    fetchSchedule();
    fetchAppointments();
  }, []);

  const fetchSchedule = async () => {
    try {
      const { data } = await axios.get(`${API}/schedule`, { withCredentials: true });
      setSchedule(data);
    } catch (error) {
      toast.error('Failed to fetch schedule');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${API}/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch (error) {
      console.error('Failed to fetch appointments');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/schedule`, schedule, { withCredentials: true });
      toast.success('Schedule saved!');
    } catch (error) {
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setSchedule({
      ...schedule,
      weekly_hours: {
        ...schedule.weekly_hours,
        [day]: { ...schedule.weekly_hours[day], enabled: !schedule.weekly_hours[day].enabled }
      }
    });
  };

  const updateDayTime = (day, field, value) => {
    setSchedule({
      ...schedule,
      weekly_hours: {
        ...schedule.weekly_hours,
        [day]: { ...schedule.weekly_hours[day], [field]: value }
      }
    });
  };

  const toggleBlockedDate = (dateStr) => {
    const blocked = schedule.blocked_dates || [];
    if (blocked.includes(dateStr)) {
      setSchedule({ ...schedule, blocked_dates: blocked.filter(d => d !== dateStr) });
    } else {
      setSchedule({ ...schedule, blocked_dates: [...blocked, dateStr] });
    }
  };

  const getAppointmentsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return appointments.filter(a => a.date === dateStr);
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  if (loading || !schedule) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="schedule-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Schedule Manager
          </h1>
          <p className="text-[#756A63] mt-1">Set your working hours and manage availability</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white"
          data-testid="save-schedule-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Weekly Hours */}
        <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
          <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#9C6A48]" />
            Weekly Working Hours
          </h2>
          <div className="space-y-4">
            {DAYS.map((day) => {
              const config = schedule.weekly_hours[day] || { enabled: false, morning_start: '09:00', morning_end: '12:00', afternoon_start: '13:00', afternoon_end: '17:00' };
              return (
                <div key={day} className="py-3 border-b border-[#E3DACD] last:border-0">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-28">
                      <span className={`text-sm font-medium ${config.enabled ? 'text-[#2B2623]' : 'text-[#756A63]'}`}>
                        {DAY_LABELS[day]}
                      </span>
                    </div>
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={() => toggleDay(day)}
                      data-testid={`toggle-${day}`}
                    />
                    {!config.enabled && <span className="text-sm text-[#756A63]">Closed</span>}
                  </div>
                  {config.enabled && (
                    <div className="ml-32 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#9C6A48] font-medium w-16">Morning</span>
                        <Input
                          type="time"
                          value={config.morning_start || '09:00'}
                          onChange={(e) => updateDayTime(day, 'morning_start', e.target.value)}
                          className="border-[#E3DACD] w-28 text-sm"
                          data-testid={`morning-start-${day}`}
                        />
                        <span className="text-[#756A63] text-xs">to</span>
                        <Input
                          type="time"
                          value={config.morning_end || '12:00'}
                          onChange={(e) => updateDayTime(day, 'morning_end', e.target.value)}
                          className="border-[#E3DACD] w-28 text-sm"
                          data-testid={`morning-end-${day}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#9C6A48] font-medium w-16">Afternoon</span>
                        <Input
                          type="time"
                          value={config.afternoon_start || '13:00'}
                          onChange={(e) => updateDayTime(day, 'afternoon_start', e.target.value)}
                          className="border-[#E3DACD] w-28 text-sm"
                          data-testid={`afternoon-start-${day}`}
                        />
                        <span className="text-[#756A63] text-xs">to</span>
                        <Input
                          type="time"
                          value={config.afternoon_end || '17:00'}
                          onChange={(e) => updateDayTime(day, 'afternoon_end', e.target.value)}
                          className="border-[#E3DACD] w-28 text-sm"
                          data-testid={`afternoon-end-${day}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Calendar - Block/Unblock Dates */}
        <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
          <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
            <CalendarOff className="w-5 h-5 text-[#9C6A48]" />
            Block Off Days
          </h2>
          <p className="text-sm text-[#756A63] mb-4">Click a date to block/unblock it. Red = blocked. Dots = booked appointments.</p>

          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="border-[#E3DACD]">
              &lt;
            </Button>
            <span className="text-sm font-medium text-[#2B2623]">{format(currentMonth, 'MMMM yyyy')}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="border-[#E3DACD]">
              &gt;
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-center text-xs text-[#756A63] font-medium py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array(startOfMonth(currentMonth).getDay()).fill(null).map((_, i) => (
              <div key={`e-${i}`} className="h-10"></div>
            ))}
            {daysInMonth.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isBlocked = (schedule.blocked_dates || []).includes(dateStr);
              const dayAppts = getAppointmentsForDay(day);
              const isToday = isSameDay(day, new Date());
              const isPast = day < new Date() && !isToday;

              return (
                <button
                  key={dateStr}
                  onClick={() => !isPast && toggleBlockedDate(dateStr)}
                  disabled={isPast}
                  className={`h-10 rounded-lg text-sm relative transition-colors ${
                    isBlocked
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : isToday
                        ? 'bg-[#9C6A48]/10 text-[#9C6A48] font-medium'
                        : isPast
                          ? 'text-[#E3DACD]'
                          : 'hover:bg-[#FAF7F2] text-[#2B2623]'
                  }`}
                  data-testid={`date-${dateStr}`}
                >
                  {format(day, 'd')}
                  {dayAppts.length > 0 && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dayAppts.slice(0, 3).map((_, i) => (
                        <span key={i} className="w-1 h-1 rounded-full bg-[#9C6A48]"></span>
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Blocked dates list */}
          {(schedule.blocked_dates || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#E3DACD]">
              <p className="text-sm font-medium text-[#2B2623] mb-2">Blocked Dates:</p>
              <div className="flex flex-wrap gap-2">
                {schedule.blocked_dates.sort().map(d => (
                  <Badge
                    key={d}
                    className="bg-red-100 text-red-700 cursor-pointer hover:bg-red-200"
                    onClick={() => toggleBlockedDate(d)}
                  >
                    {format(parseISO(d), 'MMM d, yyyy')} ×
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Today's Schedule Summary */}
      <div className="mt-8 bg-white rounded-xl border border-[#E3DACD] p-6">
        <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-[#9C6A48]" />
          Today's Appointments — {format(new Date(), 'MMMM d, yyyy')}
        </h2>
        {(() => {
          const todayStr = format(new Date(), 'yyyy-MM-dd');
          const todayAppts = appointments.filter(a => a.date === todayStr).sort((a, b) => a.time.localeCompare(b.time));
          if (todayAppts.length === 0) {
            return <p className="text-[#756A63]">No appointments scheduled for today.</p>;
          }
          return (
            <div className="divide-y divide-[#E3DACD]">
              {todayAppts.map(apt => (
                <div key={apt.id} className="py-3 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-[#2B2623]">{apt.time}</span>
                    <span className="mx-2 text-[#E3DACD]">|</span>
                    <span className="text-[#2B2623]">{apt.customer_name}</span>
                    <span className="mx-2 text-[#E3DACD]">|</span>
                    <span className="text-[#756A63]">{apt.service_name}</span>
                  </div>
                  <Badge className={
                    apt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {apt.status}
                  </Badge>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
